import React, { useEffect, useState } from 'react';
import { useTranslate } from 'piral-translate';
import { useCalendarDialogStore } from './calendarDialog.store';
import { getCalendarGrid } from '../../utils/calendar';

import './calendar-dialog.css';

const XMarkIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

export const CalendarDialog: React.FC = () => {
  const { isOpen, close } = useCalendarDialogStore();
  const translate = useTranslate();
  const [viewDate, setViewDate] = useState(new Date());

  // Reset view date when opening
  useEffect(() => {
    if (isOpen) {
      setViewDate(new Date());
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      close();
    }
  };

  const prevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const nextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  const goToToday = () => setViewDate(new Date());

  const renderCalendar = () => {
    const calendarDays = getCalendarGrid(viewDate);

    return calendarDays.map((day, index) => {
      const classes = [
        'neo-calendar-dialog-day',
        day.isToday ? 'today' : '',
        day.isWeekend ? 'weekend' : '',
        day.isHoliday ? 'holiday' : '',
        !day.isCurrentMonth ? 'other-month' : '',
      ]
        .filter(Boolean)
        .join(' ');

      return (
        <div key={index} className={classes}>
          {day.day}
        </div>
      );
    });
  };

  const monthYear = viewDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  const today = new Date();
  const isCurrentMonth = viewDate.getMonth() === today.getMonth() && viewDate.getFullYear() === today.getFullYear();

  return (
    <div className="neo-calendar-dialog-backdrop" onClick={handleBackdropClick}>
      <div className="neo-calendar-dialog">
        <div className="neo-calendar-dialog-header">
          <h2 className="neo-calendar-dialog-title">{translate('calendar')}</h2>
          <button type="button" className="neo-calendar-dialog-close" onClick={close}>
            <XMarkIcon />
          </button>
        </div>
        <div className="neo-calendar-dialog-body">
          <div className="neo-calendar-dialog-nav">
            <div className="neo-calendar-dialog-nav-left">
              <button type="button" className="neo-calendar-dialog-nav-btn" onClick={prevMonth}>
                <ChevronLeftIcon />
              </button>
              {!isCurrentMonth && (
                <button type="button" className="neo-calendar-dialog-today-btn" onClick={goToToday}>
                  {translate('today')}
                </button>
              )}
            </div>
            <span className="neo-calendar-dialog-month">{monthYear}</span>
            <button type="button" className="neo-calendar-dialog-nav-btn" onClick={nextMonth}>
              <ChevronRightIcon />
            </button>
          </div>
          <div className="neo-calendar-dialog-weekdays">
            <span>Mo</span>
            <span>Di</span>
            <span>Mi</span>
            <span>Do</span>
            <span>Fr</span>
            <span>Sa</span>
            <span>So</span>
          </div>
          <div className="neo-calendar-dialog-days">{renderCalendar()}</div>
        </div>
      </div>
    </div>
  );
};
